import React, { useEffect, useMemo, useCallback, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Link as LinkIcon,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  RemoveFormatting,
  ChevronDown,
  Image as ImageIcon,
  Trash2,
  Replace,
  ExternalLink,
  Copy,
  Download,
  LibraryBig,
} from 'lucide-react';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import FileHandler from '@tiptap/extension-file-handler';
import Underline from '@tiptap/extension-underline';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Typography from '@tiptap/extension-typography';
import CharacterCount from '@tiptap/extension-character-count';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { generateRTEJSON, traceRTEDocumentWhitespace, type RTEDocument } from '@/components/blog-templates';

import { TipTapToolbar } from './TipTapToolbar';
import { ImageDetailsPopover } from './ImageDetailsPopover';
import { ImageRegeneratePopover } from './ImageRegeneratePopover';
import { ImageAssetPickerDialog } from './ImageAssetPickerDialog';
import { AIContextMenu, type AIActionType } from './AIContextMenu';
import { BlockGutterOverlay } from './BlockGutter';
import './styles/template-content.css';
import {
  CalloutExtension,
  FaqSchemaExtension,
  FaqItemExtension,
  FaqQuestionExtension,
  FaqAnswerExtension,
  StatsGridExtension,
  StatItemExtension,
  CtaExtension,
  FocusTermHighlight,
  BlockGutterExtension,
  ImageExtension,
  BlockIdAttribute,
  RedlineMark,
  SuggestionMark,
  TocScrollExtension,
  type BlockGutterState,
} from './extensions';
import { useTipTapAutoSave } from './hooks/useTipTapAutoSave';
import { useFocusTerms } from './hooks/useFocusTerms';
import { getKeywordSuggestion, type KeywordMetric } from './keywordSuggestion';

export interface TipTapBlogEditorProps {
  contentId: string;
  /** Template ID associated with this content (for CSS loading in future) */
  templateId?: string;
  /** HTML content (fallback for legacy content) */
  initialContent?: string;
  /** JSON content (preferred format - loads directly into TipTap) */
  initialContentJson?: RTEDocument;
  focusTerms?: string[];
  /**
   * Optional keyword metrics used to enrich the inline hover-suggestion tooltip
   * shown over highlighted focus terms. Fully optional — when omitted the
   * tooltip falls back to occurrence-count-based tips. NOTE: must NOT come from
   * Google Ads APIs (e.g. Ahrefs/DataForSEO/SERP data is fine).
   */
  targetKeywords?: KeywordMetric[];
  readOnly?: boolean;
  /** Save callback receives JSON, HTML, and word count - frontend is authoritative for word count */
  onSave?: (json: RTEDocument, html: string, wordCount: number) => Promise<void>;
  onChange?: (html: string) => void;
  onTermCountChange?: (counts: Record<string, number>) => void;
  onAIAction?: (action: AIActionType, options?: { tone?: string; prompt?: string; selectedText?: string }) => Promise<void>;
  autoSave?: boolean;
  autoSaveDebounceMs?: number;
  className?: string;
  placeholder?: string;
  showAIBar?: boolean;
  /** Callback when H1 heading content changes (for bidirectional title sync) */
  onH1Change?: (h1Text: string) => void;
  /** Callback receiving block gutter state updates (for comment indicators) */
  onBlockGutterUpdate?: (state: BlockGutterState) => void;
  /**
   * Suppress the built-in TipTapToolbar so a host page (e.g. UniversalEditorPage's
   * DocChrome FormatToolbar) can drive formatting via the editor ref instead.
   * Defaults to false to preserve existing call sites.
   */
  hideInternalToolbar?: boolean;
  /**
   * Fired once the TipTap editor instance is created. Lets a host page mount
   * editor-bound overlays (BubbleMenu, selection menu) without polling
   * editorRef.current?.getEditor().
   */
  onEditorReady?: (editor: import('@tiptap/react').Editor) => void;
}

export interface TipTapBlogEditorRef {
  saveNow: () => Promise<void>;
  getHTML: () => string;
  getJSON: () => RTEDocument;
  focus: () => void;
  isEmpty: () => boolean;
  /** Insert text at the current cursor position */
  insertTextAtCursor: (text: string) => void;
  /** Update the H1 heading content programmatically */
  updateH1: (text: string) => void;
  /** Get the raw TipTap editor instance (for toolbar integration) */
  getEditor: () => import('@tiptap/react').Editor | null;
}

export const TipTapBlogEditor = forwardRef<TipTapBlogEditorRef, TipTapBlogEditorProps>(function TipTapBlogEditor({
  contentId,
  templateId,
  initialContent = '',
  initialContentJson,
  focusTerms: initialFocusTerms = [],
  targetKeywords,
  readOnly = false,
  onSave,
  onChange,
  onTermCountChange,
  onAIAction,
  autoSave = true,
  autoSaveDebounceMs = 2000,
  className,
  placeholder = 'Start writing your blog content...',
  showAIBar = true,
  onH1Change,
  onBlockGutterUpdate,
  hideInternalToolbar = false,
  onEditorReady,
}, ref) {
  const [parseError, setParseError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [blockGutterState, setBlockGutterState] = useState<BlockGutterState | null>(null);
  const onBlockGutterUpdateRef = useRef(onBlockGutterUpdate);
  onBlockGutterUpdateRef.current = onBlockGutterUpdate;
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;
  useEffect(() => {
    if (blockGutterState) onBlockGutterUpdateRef.current?.(blockGutterState);
  }, [blockGutterState]);
  // Normalize focus terms once (lowercase, trimmed, deduped, non-empty) so the
  // highlight decorations reliably render and their `data-focus-term` values
  // match what the hover-suggestion handler looks up. Memoized on the term
  // *contents* (not array identity) so callers passing a fresh array literal on
  // every render don't churn the editor extensions / drop decorations.
  const normalizedFocusTerms = useMemo(
    () =>
      Array.from(
        new Set(
          (initialFocusTerms || [])
            .map((t) => (t || '').trim().toLowerCase())
            .filter(Boolean)
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify((initialFocusTerms || []).map((t) => (t || '').trim().toLowerCase()))]
  );

  const initialContentRef = useRef(initialContent);
  const initialContentJsonRef = useRef(initialContentJson);
  const hasSetInitialContent = useRef(false);
  // Track if content update is programmatic (to avoid triggering auto-save)
  const isProgrammaticUpdateRef = useRef(false);

  // DEBUG: Log when content props change
  useEffect(() => {
    console.group('[FORMATTING_TRACE] TipTapBlogEditor - Content props received');
    console.log('[FORMATTING_TRACE] Content ID:', contentId);
    console.log('[FORMATTING_TRACE] Has initialContentJson:', !!initialContentJson);
    console.log('[FORMATTING_TRACE] Has initialContent (HTML):', !!initialContent);
    console.log('[FORMATTING_TRACE] initialContent length:', initialContent?.length || 0);
    if (initialContentJson) {
      console.log('[FORMATTING_TRACE] JSON content nodes:', initialContentJson.content?.length || 0);
      // Log first few nodes to check for formatting attrs
      console.log('[FORMATTING_TRACE] First 5 nodes with attrs:');
      initialContentJson.content?.slice(0, 5).forEach((node, i) => {
        console.log(`[FORMATTING_TRACE]   Node ${i}: type=${node.type}, blockId=${node.attrs?.blockId || 'NONE'}, textAlign=${node.attrs?.textAlign || 'NONE'}`);
        console.log(`[FORMATTING_TRACE]   Node ${i} full attrs:`, node.attrs);
        // Check for text marks (bold, italic, etc.)
        if (node.content) {
          node.content.forEach((child: any, j: number) => {
            if (child.marks && child.marks.length > 0) {
              console.log(`[FORMATTING_TRACE]   Node ${i} child ${j} marks:`, child.marks);
            }
          });
        }
      });
      // Specifically trace image nodes for debugging alignment/dimensions
      const imageNodes = initialContentJson.content?.filter((node) => node.type === 'image') || [];
      if (imageNodes.length > 0) {
        console.group('[IMAGE_EXT_TRACE] Image nodes in initialContentJson:');
        imageNodes.forEach((node, i) => {
          console.log(`[IMAGE_EXT_TRACE]   Image ${i}: blockId=${node.attrs?.blockId || 'NONE'}`);
          console.log(`[IMAGE_EXT_TRACE]   Image ${i} attrs:`, {
            src: node.attrs?.src?.substring(0, 60) + '...',
            alignment: node.attrs?.alignment,
            width: node.attrs?.width,
            height: node.attrs?.height,
            aspectRatio: node.attrs?.aspectRatio,
            isAIGenerated: node.attrs?.isAIGenerated,
          });
        });
        console.groupEnd();
      }
    }
    if (initialContent) {
      console.log('[FORMATTING_TRACE] initialContent preview:', initialContent.substring(0, 500));
      const blockIdMatches = initialContent.match(/data-block-id="[^"]+"/g);
      console.log('[FORMATTING_TRACE] Block IDs in initialContent:', blockIdMatches?.length || 0, blockIdMatches?.slice(0, 5));
    }
    console.groupEnd();
  }, [initialContent, initialContentJson, contentId]);

  // Resolve initial JSON - prefer direct JSON, fallback to HTML parsing
  const initialJSON = useMemo(() => {
    // Prefer JSON if available (no parsing needed!)
    if (initialContentJson) {
      traceRTEDocumentWhitespace('TipTapBlogEditor:initial_json_path', initialContentJson, {
        content_id: contentId,
        has_html_fallback: !!initialContent,
      });
      console.log('[FORMATTING_TRACE] TipTapBlogEditor - Using initialContentJson directly (JSON path)');
      console.log('[FORMATTING_TRACE] initialContentJson being used for editor:', JSON.stringify(initialContentJson, null, 2).substring(0, 2000));
      setParseError(null);
      return initialContentJson;
    }

    // Fallback to HTML parsing for legacy content
    if (initialContent) {
      console.warn('[TipTapBlogEditor] Using HTML fallback - content should be migrated to JSON');
      try {
        setParseError(null);
        console.group('[TipTapBlogEditor] Parsing HTML to RTE JSON');
        console.log('Input HTML length:', initialContent.length);
        const json = generateRTEJSON(initialContent);
        traceRTEDocumentWhitespace('TipTapBlogEditor:html_fallback_parsed', json, {
          content_id: contentId,
          html_length: initialContent.length,
        });
        console.log('Parsed JSON:', json);
        console.log('JSON content nodes:', json?.content?.length || 0);
        if (json?.content) {
          json.content.slice(0, 5).forEach((node, i) => {
            console.log(`Node ${i}:`, node.type, node.attrs?.blockId || 'no-block-id');
          });
        }
        console.groupEnd();
        return json;
      } catch (err) {
        console.error('[TipTapBlogEditor] Failed to parse initial content:', err);
        setParseError('Failed to parse content. The editor will start empty.');
        return undefined;
      }
    }

    return undefined;
  }, [initialContentJson, initialContent, contentId]);

  // Configure extensions - memoize to prevent recreation
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      ImageExtension.configure({
        allowBase64: true,
        HTMLAttributes: {
          loading: 'lazy',
        },
        resize: {
          enabled: true,
          directions: ['bottom-right', 'bottom-left', 'top-right', 'top-left'],
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      FileHandler.configure({
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'],
        onDrop: (currentEditor, files, pos) => {
          for (const file of files) {
            console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] DROP - image dropped', {
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              position: pos,
            });
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              const src = reader.result as string;
              console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] INSERT - image inserted from drop', {
                src: src.startsWith('data:') ? `${src.substring(0, 50)}... (base64, ${src.length} chars)` : src,
                alt: file.name,
              });
              currentEditor.chain().insertContentAt(pos, {
                type: 'image',
                attrs: { src },
              }).focus().run();
            };
          }
        },
        onPaste: (currentEditor, files) => {
          for (const file of files) {
            console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] PASTE - image pasted', {
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            });
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              const src = reader.result as string;
              console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] INSERT - image inserted from paste', {
                src: src.startsWith('data:') ? `${src.substring(0, 50)}... (base64, ${src.length} chars)` : src,
                alt: file.name,
              });
              currentEditor.chain().insertContent({
                type: 'image',
                attrs: { src },
              }).focus().run();
            };
          }
        },
      }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Typography,
      CharacterCount,
      // Table extensions
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'tiptap-table',
        },
      }),
      TableRow,
      TableCell,
      TableHeader,
      // Custom extensions
      BlockIdAttribute,
      RedlineMark,
      SuggestionMark,
      TocScrollExtension,
      CalloutExtension,
      FaqSchemaExtension,
      FaqItemExtension,
      FaqQuestionExtension,
      FaqAnswerExtension,
      StatsGridExtension,
      StatItemExtension,
      CtaExtension,
      BlockGutterExtension.configure({
        onUpdate: setBlockGutterState,
      }),
      FocusTermHighlight.configure({
        focusTerms: normalizedFocusTerms,
        highlightClass: 'focus-term-highlight',
        onTermCountChange,
      }),
    ],
    [placeholder, normalizedFocusTerms, onTermCountChange, setBlockGutterState]
  );

  // Initialize editor
  // immediatelyRender: false fixes React StrictMode double-mount warning
  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content: initialJSON,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none',
          'min-h-[500px] p-6',
          '[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4',
          '[&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mb-3',
          '[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:!mt-4',
          '[&_h4]:text-lg [&_h4]:font-medium [&_h4]:mb-2',
          '[&_p]:mb-4 [&_p]:leading-relaxed',
          '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-2 [&_ul]:mt-0',
          '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-2 [&_ol]:mt-0',
          '[&_li]:mb-0.5 [&_li]:leading-normal',
          '[&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4',
          '[&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm',
          '[&_pre]:bg-zinc-900 dark:[&_pre]:bg-zinc-950 [&_pre]:text-zinc-100 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto',
          '[&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800',
          '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg',
          '[&_hr]:my-6 [&_hr]:border-border'
        ),
      },
    },
    onCreate: ({ editor }) => {
      console.group('[FORMATTING_TRACE] TipTapBlogEditor - Editor Created');
      console.log('[FORMATTING_TRACE] Editor initialized with content');
      console.log('[FORMATTING_TRACE] Editor HTML preview:', editor.getHTML().substring(0, 1000));
      console.log('[FORMATTING_TRACE] Editor isEmpty:', editor.isEmpty);
      // Check editor JSON for formatting
      const editorJson = editor.getJSON();
      traceRTEDocumentWhitespace('TipTapBlogEditor:editor_created', editorJson as RTEDocument, {
        content_id: contentId,
      });
      console.log('[FORMATTING_TRACE] Editor JSON content count:', editorJson.content?.length || 0);
      editorJson.content?.slice(0, 5).forEach((node: any, i: number) => {
        console.log(`[FORMATTING_TRACE]   Editor Node ${i}: type=${node.type}, textAlign=${node.attrs?.textAlign || 'NONE'}`);
        console.log(`[FORMATTING_TRACE]   Editor Node ${i} attrs:`, node.attrs);
      });
      console.groupEnd();
      setIsInitialized(true);
      hasSetInitialContent.current = true;
      onEditorReadyRef.current?.(editor);
    },
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML();
        onChange(html);
      }

      // Detect H1 changes and notify parent for bidirectional title sync
      if (onH1Change) {
        const json = editor.getJSON();
        const h1Node = json.content?.find(
          (node: any) => node.type === 'heading' && node.attrs?.level === 1
        );
        const h1Text = h1Node?.content?.[0]?.text || '';
        onH1Change(h1Text);
      }
    },
  });

  // Keep latest targetKeywords in a ref so the hover listeners (attached once)
  // always read fresh metrics without re-binding on every prop change.
  const targetKeywordsRef = useRef(targetKeywords);
  targetKeywordsRef.current = targetKeywords;

  // ── Inline hover suggestions for highlighted focus terms ──────────────────
  // When the user hovers a `.focus-term-highlight` span (decorated by the
  // FocusTermHighlight extension with a `data-focus-term` attribute), show a
  // small floating tooltip positioned at the element with a suggestion snippet.
  // Implemented with event delegation on the editor DOM + a lazily-created
  // floating div so it stays self-contained and cheap.
  useEffect(() => {
    if (!editor) return;
    const editorDom = editor.view.dom as HTMLElement;
    if (!editorDom) return;

    let tooltipEl: HTMLDivElement | null = null;
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let activeTarget: HTMLElement | null = null;

    const ensureTooltip = (): HTMLDivElement => {
      if (tooltipEl) return tooltipEl;
      const el = document.createElement('div');
      el.className = 'focus-term-suggestion-tooltip';
      el.setAttribute('role', 'tooltip');
      el.style.display = 'none';
      document.body.appendChild(el);
      tooltipEl = el;
      return el;
    };

    const hideTooltip = () => {
      if (showTimer) {
        clearTimeout(showTimer);
        showTimer = null;
      }
      activeTarget = null;
      if (tooltipEl) tooltipEl.style.display = 'none';
    };

    const positionTooltip = (el: HTMLDivElement, target: HTMLElement) => {
      const rect = target.getBoundingClientRect();
      // Render first (off-screen) so we can measure, then place above the term.
      el.style.visibility = 'hidden';
      el.style.display = 'block';
      const tipRect = el.getBoundingClientRect();
      const margin = 8;
      let top = rect.top - tipRect.height - margin;
      // If not enough room above, place below the term.
      if (top < margin) top = rect.bottom + margin;
      let left = rect.left + rect.width / 2 - tipRect.width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - tipRect.width - margin));
      el.style.top = `${Math.round(top + window.scrollY)}px`;
      el.style.left = `${Math.round(left + window.scrollX)}px`;
      el.style.visibility = 'visible';
    };

    const handleMouseOver = (event: Event) => {
      const rawTarget = event.target as HTMLElement | null;
      const target = rawTarget?.closest?.('.focus-term-highlight') as HTMLElement | null;
      if (!target) return;
      if (target === activeTarget) return;
      activeTarget = target;

      const term = target.getAttribute('data-focus-term') || target.textContent || '';
      if (!term.trim()) return;

      if (showTimer) clearTimeout(showTimer);
      showTimer = setTimeout(() => {
        // Bail if the user moved off the term before the delay elapsed.
        if (activeTarget !== target) return;
        // Count occurrences in the live DOM (cheap, reflects current doc).
        const occurrences = editorDom.querySelectorAll(
          `.focus-term-highlight[data-focus-term="${(window.CSS?.escape ? CSS.escape(term.toLowerCase()) : term.toLowerCase())}"]`
        ).length;
        // Document word count (for a keyword-density estimate). CharacterCount
        // exposes words() on its storage; fall back gracefully if unavailable.
        let totalWords: number | undefined;
        try {
          totalWords = editor.storage?.characterCount?.words?.();
        } catch {
          totalWords = undefined;
        }
        const text = getKeywordSuggestion({
          term: term.toLowerCase(),
          occurrences: occurrences || 1,
          metrics: targetKeywordsRef.current,
          totalWords,
        });
        const el = ensureTooltip();
        el.textContent = text;
        positionTooltip(el, target);
      }, 180);
    };

    const handleMouseOut = (event: Event) => {
      const related = (event as MouseEvent).relatedTarget as HTMLElement | null;
      // Ignore moves that stay within the same highlight span.
      if (related && activeTarget && activeTarget.contains(related)) return;
      hideTooltip();
    };

    editorDom.addEventListener('mouseover', handleMouseOver);
    editorDom.addEventListener('mouseout', handleMouseOut);
    // Backstop: leaving the editor area (or the hovered highlight getting
    // removed/re-rendered on an edit) should always dismiss the tooltip so it
    // never gets stuck floating over the page.
    editorDom.addEventListener('mouseleave', hideTooltip);

    return () => {
      editorDom.removeEventListener('mouseover', handleMouseOver);
      editorDom.removeEventListener('mouseout', handleMouseOut);
      editorDom.removeEventListener('mouseleave', hideTooltip);
      if (showTimer) clearTimeout(showTimer);
      if (tooltipEl && tooltipEl.parentNode) {
        tooltipEl.parentNode.removeChild(tooltipEl);
      }
      tooltipEl = null;
    };
  }, [editor]);

  // Handle initialContent changes after mount
  useEffect(() => {
    if (!editor || !isInitialized) return;

    // Only update if content actually changed and we've already initialized
    if (initialContent !== initialContentRef.current && hasSetInitialContent.current) {
      console.group('[TipTapBlogEditor] Content Changed After Mount');
      console.log('Previous content length:', initialContentRef.current?.length || 0);
      console.log('New content length:', initialContent?.length || 0);

      initialContentRef.current = initialContent;

      // Mark as programmatic update to prevent auto-save from triggering
      isProgrammaticUpdateRef.current = true;

      if (initialContent) {
        try {
          console.log('Parsing new content to RTE JSON...');
          const newJSON = generateRTEJSON(initialContent);
          traceRTEDocumentWhitespace('TipTapBlogEditor:updated_html_parsed', newJSON, {
            content_id: contentId,
            html_length: initialContent.length,
          });
          console.log('New JSON nodes:', newJSON?.content?.length || 0);
          // Use setContent to replace all content
          editor.commands.setContent(newJSON || '');
          console.log('Content set in editor. New HTML preview:', editor.getHTML().substring(0, 500));
          setParseError(null);
        } catch (err) {
          console.error('[TipTapBlogEditor] Failed to parse updated content:', err);
          setParseError('Failed to parse updated content.');
          isProgrammaticUpdateRef.current = false;
        }
      } else {
        console.log('Clearing content (no initialContent)');
        editor.commands.clearContent();
      }
      console.groupEnd();
    }
  }, [editor, initialContent, isInitialized]);

  // Handle initialContentJson changes after mount (e.g. after quality assist apply)
  useEffect(() => {
    if (!editor || !isInitialized) return;
    if (!initialContentJson || !hasSetInitialContent.current) return;

    // Compare by reference — React Query returns a new object on refetch
    if (initialContentJson === initialContentJsonRef.current) return;
    initialContentJsonRef.current = initialContentJson;

    console.log('[TipTapBlogEditor] JSON content changed after mount — refreshing editor');
    traceRTEDocumentWhitespace('TipTapBlogEditor:updated_json_path', initialContentJson, {
      content_id: contentId,
    });
    isProgrammaticUpdateRef.current = true;
    editor.commands.setContent(initialContentJson);
  }, [editor, initialContentJson, isInitialized, contentId]);

  // Focus terms management
  const {
    focusTerms,
    termCounts,
    setFocusTerms,
  } = useFocusTerms({
    editor,
    initialTerms: normalizedFocusTerms,
  });

  // Wrapped onSave that skips saves during programmatic updates
  const wrappedOnSave = useCallback(async (json: RTEDocument, html: string, wordCount: number) => {
    // Skip save if this was triggered by programmatic content update
    if (isProgrammaticUpdateRef.current) {
      isProgrammaticUpdateRef.current = false;
      return;
    }
    if (onSave) {
      await onSave(json, html, wordCount);
    }
  }, [onSave]);

  // Auto-save functionality - only enable after initial content is set
  const { isSaving, lastSaved, saveNow, error: saveError, hasUnsavedChanges } = useTipTapAutoSave({
    editor,
    onSave: wrappedOnSave,
    debounceMs: autoSaveDebounceMs,
    enabled: autoSave && !!onSave && isInitialized,
  });

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    saveNow,
    getEditor: () => editor,
    getHTML: () => editor?.getHTML() || '',
    getJSON: () => (editor?.getJSON() as RTEDocument) || { type: 'doc', content: [] },
    focus: () => editor?.commands.focus(),
    isEmpty: () => editor?.isEmpty ?? true,
    insertTextAtCursor: (text: string) => {
      if (!editor) return;
      // Focus the editor first to ensure we have a valid selection
      editor.commands.focus();
      // Insert text at the current cursor position
      editor.commands.insertContent(text);
    },
    updateH1: (text: string) => {
      if (!editor) return;

      const { doc } = editor.state;
      let h1Pos: number | null = null;
      let h1NodeSize: number = 0;

      // Find the first H1 node position
      doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.level === 1) {
          h1Pos = pos;
          h1NodeSize = node.nodeSize;
          return false; // stop searching
        }
      });

      if (h1Pos !== null) {
        // Replace existing H1 content
        const newH1 = editor.schema.nodes.heading.create(
          { level: 1 },
          text ? editor.schema.text(text) : null
        );

        const tr = editor.state.tr.replaceWith(h1Pos, h1Pos + h1NodeSize, newH1);
        editor.view.dispatch(tr);
      } else {
        // No H1 exists - insert one at the start
        editor.commands.insertContentAt(0, {
          type: 'heading',
          attrs: { level: 1 },
          content: text ? [{ type: 'text', text }] : [],
        });
      }
    },
  }), [editor, saveNow]);

  // Update editable state when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Update focus terms when prop changes
  useEffect(() => {
    if (initialFocusTerms.length > 0) {
      setFocusTerms(initialFocusTerms);
    }
  }, [initialFocusTerms, setFocusTerms]);

  // Notify parent of term count changes
  useEffect(() => {
    if (onTermCountChange && Object.keys(termCounts).length > 0) {
      onTermCountChange(termCounts);
    }
  }, [termCounts, onTermCountChange]);

  // Get character count safely
  const characterCount = editor?.storage.characterCount?.characters?.() ?? 0;
  const wordCount = editor?.storage.characterCount?.words?.() ?? 0;

  // AI Action state
  const [isAIProcessing, setIsAIProcessing] = useState(false);

  // Handle AI actions with selected text context
  const handleAIAction = useCallback(async (
    action: AIActionType,
    options?: { tone?: string; prompt?: string }
  ) => {
    if (!onAIAction || !editor) return;

    // Get selected text if any
    const { from, to } = editor.state.selection;
    const selectedText = from !== to ? editor.state.doc.textBetween(from, to, ' ') : '';

    setIsAIProcessing(true);
    try {
      await onAIAction(action, { ...options, selectedText });
    } finally {
      setIsAIProcessing(false);
    }
  }, [onAIAction, editor]);

  // Set link handler for bubble menu
  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <Card className={cn('tiptap-blog-editor flex flex-col', className)}>
      {/* Parse Error Banner */}
      {parseError && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-yellow-800 text-sm shrink-0">
          {parseError}
        </div>
      )}

      {/* Toolbar - Fixed at top. Hidden when host chrome owns formatting (DocChrome). */}
      {!readOnly && !hideInternalToolbar && (
        // data-editor-sticky-chrome (TOC_STICKY_CHROME_ATTR in TocScrollExtension) lets the
        // TOC click handler measure this toolbar's height and offset the scroll target below it.
        <div className="shrink-0 sticky top-0 z-20 bg-background" data-editor-sticky-chrome="">
          <TipTapToolbar
            editor={editor}
            showAIBar={showAIBar && !!onAIAction}
            onAIAction={handleAIAction}
            isAIProcessing={isAIProcessing}
          />
        </div>
      )}

      {/* Editor Content with Block Gutter and Context Menu - Scrollable */}
      <div className="relative flex-1 overflow-auto">
        {/* Interactive Block Gutter - hidden */}

        {/* Editor Content with optional AI Context Menu */}
        {onAIAction ? (
          <AIContextMenu
            editor={editor}
            onAIAction={handleAIAction}
            isProcessing={isAIProcessing}
          >
            <div className="editor-content-wrapper template-content">
              <EditorContent editor={editor} />
            </div>
          </AIContextMenu>
        ) : (
          <div className="template-content">
            <EditorContent editor={editor} />
          </div>
        )}
      </div>

      {/* Bubble Menu - Floating toolbar that appears on text selection */}
      {editor && !readOnly && (
        <BubbleMenu
          editor={editor}
          appendTo={() => document.body}
          updateDelay={0}
          shouldShow={({ editor: ed, view }) => {
            // Don't show if editor is not editable
            if (!ed.isEditable) return false;

            // Only show when there's text selection
            const { from, to } = ed.state.selection;
            const hasTextSelection = from !== to;

            // Also show when interacting with the menu or popovers
            const activeElement = document.activeElement;
            const hasEditorFocus = view.hasFocus();
            const isInBubbleMenu = activeElement?.closest('.bubble-menu-toolbar');
            const isInRadixPopover = activeElement?.closest('[data-radix-popper-content-wrapper]');

            return (hasTextSelection && hasEditorFocus) || !!isInBubbleMenu || !!isInRadixPopover;
          }}
          options={{
            strategy: 'fixed',
            placement: 'top',
            offset: 10,
            flip: true,
            shift: { padding: 8 },
          }}
          className="bubble-menu-toolbar"
        >
          <div className="flex items-center gap-0.5 bg-white border border-border rounded-lg shadow-lg px-1.5 py-1">
            {/* Text Formatting */}
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('bold')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Bold (Ctrl+B)"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('italic')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Italic (Ctrl+I)"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('underline')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('strike')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Strikethrough"
            >
              <Strikethrough className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Link */}
            <button
              type="button"
              onClick={setLink}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('link')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Add Link"
            >
              <LinkIcon className="w-4 h-4" />
            </button>

            {/* Highlight */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'p-1.5 rounded transition-colors',
                    editor.isActive('highlight')
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                  title="Highlight"
                >
                  <Highlighter className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="start">
                <div className="grid grid-cols-4 gap-1">
                  {['#FEFCE8', '#FEF3C7', '#FDE68A', '#D9F99D', '#BBF7D0', '#A7F3D0', '#BAE6FD', '#DDD6FE'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => editor.chain().focus().toggleHighlight({ color }).run()}
                      className="w-6 h-6 rounded border border-border hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().unsetHighlight().run()}
                  className="w-full mt-2 px-2 py-1 text-xs text-muted-foreground hover:bg-muted rounded"
                >
                  Remove highlight
                </button>
              </PopoverContent>
            </Popover>

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Alignment */}
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive({ textAlign: 'left' })
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive({ textAlign: 'center' })
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive({ textAlign: 'right' })
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Align Right"
            >
              <AlignRight className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Lists */}
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('bulletList')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.isActive('orderedList')
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Numbered List"
            >
              <ListOrdered className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Clear Formatting */}
            <button
              type="button"
              onClick={() => editor.chain().focus().unsetAllMarks().run()}
              className="p-1.5 rounded transition-colors text-muted-foreground hover:bg-muted"
              title="Clear Formatting"
            >
              <RemoveFormatting className="w-4 h-4" />
            </button>
          </div>
        </BubbleMenu>
      )}

      {/* Image Bubble Menu - Floating toolbar that appears when image is selected */}
      {/* Unmount while the asset picker is open — tiptap only re-runs shouldShow on
          editor transactions, not when assetPickerOpen flips, so a shouldShow guard
          can't hide it; it would keep floating (z-9999) over the dialog's tiles. */}
      {editor && !readOnly && !assetPickerOpen && (
        <BubbleMenu
          editor={editor}
          appendTo={() => document.body}
          updateDelay={0}
          shouldShow={({ editor: ed }) => {
            // Hide while the asset-picker dialog is open — its z-9999 !important
            // menu would otherwise float over the z-50 shadcn dialog.
            if (assetPickerOpen) return false;
            // Only show when an image is selected
            return ed.isActive('image');
          }}
          options={{
            strategy: 'fixed',
            placement: 'top',
            offset: 10,
            flip: true,
            shift: { padding: 8 },
          }}
          className="image-bubble-menu"
        >
          <div className="flex items-center gap-1 bg-white border border-border rounded-lg shadow-lg px-2 py-1.5">
            {/* Image indicator */}
            <div className="flex items-center gap-1.5 px-2 py-1 bg-muted rounded text-muted-foreground mr-1">
              <ImageIcon className="w-4 h-4" />
              <span className="text-xs font-medium">Image</span>
            </div>

            <div className="w-px h-5 bg-accent" />

            {/* Alignment buttons */}
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { alignment: 'left' }).run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.getAttributes('image').alignment === 'left'
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { alignment: 'center' }).run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.getAttributes('image').alignment === 'center'
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { alignment: 'right' }).run()}
              className={cn(
                'p-1.5 rounded transition-colors',
                editor.getAttributes('image').alignment === 'right'
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
              title="Align Right"
            >
              <AlignRight className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Replace Image */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="p-1.5 rounded transition-colors text-muted-foreground hover:bg-muted"
                  title="Replace Image"
                >
                  <Replace className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-3" align="start">
                <div className="space-y-3">
                  <Label htmlFor="image-url" className="text-xs font-medium">Image URL</Label>
                  <Input
                    id="image-url"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    defaultValue={editor.getAttributes('image').src || ''}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const value = (e.target as HTMLInputElement).value;
                        if (value) {
                          editor.chain().focus().updateAttributes('image', { src: value }).run();
                        }
                      }
                    }}
                    className="text-sm"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = document.getElementById('image-url') as HTMLInputElement;
                      if (input?.value) {
                        editor.chain().focus().updateAttributes('image', { src: input.value }).run();
                      }
                    }}
                    className="w-full px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                  >
                    Update Image
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Choose from asset library */}
            <button
              type="button"
              onClick={() => setAssetPickerOpen(true)}
              className="p-1.5 rounded transition-colors text-muted-foreground hover:bg-muted"
              title="Choose from asset library"
            >
              <LibraryBig className="w-4 h-4" />
            </button>

            {/* Regenerate with AI */}
            <ImageRegeneratePopover editor={editor} focusTerms={focusTerms} />

            {/* Image details: alt text (semantic metadata) + caption (visible copy) */}
            <ImageDetailsPopover editor={editor} />

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Open in new tab */}
            <button
              type="button"
              onClick={() => {
                const src = editor.getAttributes('image').src;
                if (src) window.open(src, '_blank');
              }}
              className="p-1.5 rounded transition-colors text-muted-foreground hover:bg-muted"
              title="Open in New Tab"
            >
              <ExternalLink className="w-4 h-4" />
            </button>

            {/* Copy URL */}
            <button
              type="button"
              onClick={() => {
                const src = editor.getAttributes('image').src;
                if (src) {
                  navigator.clipboard.writeText(src);
                }
              }}
              className="p-1.5 rounded transition-colors text-muted-foreground hover:bg-muted"
              title="Copy Image URL"
            >
              <Copy className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-accent mx-1" />

            {/* Delete */}
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteSelection().run()}
              className="p-1.5 rounded transition-colors text-red-600 hover:bg-red-50"
              title="Delete Image"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </BubbleMenu>
      )}

      {/* Status Bar */}
      {autoSave && onSave && (
        <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground bg-muted/20">
          <div className="flex items-center gap-2">
            {isSaving && (
              <span className="animate-pulse">Saving...</span>
            )}
            {!isSaving && lastSaved && (
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            )}
            {!isSaving && hasUnsavedChanges && !lastSaved && (
              <span className="text-yellow-600">Unsaved changes</span>
            )}
            {saveError && (
              <span className="text-destructive">Save failed: {saveError.message}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {focusTerms.length > 0 && (
              <span>
                Focus terms: {Object.values(termCounts).reduce((a, b) => a + b, 0)} occurrences
              </span>
            )}
            <span>{wordCount} words</span>
            <span>{characterCount} characters</span>
          </div>
        </div>
      )}

      {/* Asset library picker — mounted outside the image BubbleMenu so it
          stays open even when the image selection changes. */}
      <ImageAssetPickerDialog
        open={assetPickerOpen}
        onOpenChange={setAssetPickerOpen}
        onPick={(sel) => {
          if (!editor) return;
          editor
            .chain()
            .focus()
            .updateAttributes('image', {
              src: sel.url,
              alt: sel.alt || editor.getAttributes('image').alt,
              isAIGenerated: false,
            })
            .run();
        }}
      />

      {/* Custom styles */}
      <style>{`
        .tiptap-blog-editor .ProseMirror {
          outline: none;
          padding-left: 1rem !important;
        }

        /* Consistent link styling - override textStyle inline color on <span> children inside <a> */
        .tiptap-blog-editor .ProseMirror a,
        .tiptap-blog-editor .ProseMirror a span,
        .tiptap-blog-editor .ProseMirror a * {
          color: var(--link-color, #2563eb) !important;
          text-decoration: underline !important;
          cursor: pointer;
        }

        .tiptap-blog-editor .ProseMirror a:hover,
        .tiptap-blog-editor .ProseMirror a:hover span,
        .tiptap-blog-editor .ProseMirror a:hover * {
          opacity: 0.8;
        }

        .tiptap-blog-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }

        /* Block gutter styles - hidden */

        .tiptap-blog-editor .focus-term-highlight {
          background-color: #fef3c7;
          font-weight: 600;
          padding: 0 2px;
          border-radius: 2px;
          cursor: help;
        }

        /*
         * Inline focus-term hover suggestion tooltip. Appended to <body> by the
         * hover effect in this component, so it is NOT scoped under
         * .tiptap-blog-editor. Uses theme tokens for light/dark consistency.
         */
        .focus-term-suggestion-tooltip {
          position: absolute;
          z-index: 9999;
          max-width: 280px;
          padding: 8px 10px;
          font-size: 12px;
          line-height: 1.4;
          color: var(--color-foreground, #1f2937);
          background-color: var(--color-card, var(--color-warm-bg));
          border: 1px solid var(--color-border, #e5e7eb);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          pointer-events: none;
        }

        .tiptap-blog-editor .callout {
          border-radius: 0.5rem;
          padding: 1rem;
          margin: 1rem 0;
        }

        .tiptap-blog-editor .callout-info,
        .tiptap-blog-editor .callout-callout {
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-left: 4px solid #64748b;
          border-radius: 0 10px 10px 0;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
        }

        .tiptap-blog-editor .callout-table-of-contents {
          background: transparent;
          border-top: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          border-left: 0;
          border-radius: 0;
          padding: 0.75rem 0;
          margin: 1rem 0 1.25rem;
        }

        .tiptap-blog-editor .callout-table-of-contents :is(h2, h3) {
          font-size: 1rem;
          line-height: 1.35;
          margin: 0 0 0.5rem;
        }

        .tiptap-blog-editor .callout-table-of-contents ul {
          list-style: none;
          margin: 0;
          padding-left: 0;
        }

        .tiptap-blog-editor .callout-table-of-contents ul.table-of-contents-list {
          padding-left: 0.75rem;
        }

        .tiptap-blog-editor .callout-table-of-contents ul.table-of-contents-sublist {
          margin: 0.35rem 0 0.15rem 0.75rem;
          padding-left: 0.75rem;
          border-left: 1px solid #e2e8f0;
        }

        .tiptap-blog-editor .callout-table-of-contents li {
          margin: 0.35rem 0;
          line-height: 1.4;
        }

        .tiptap-blog-editor .callout-table-of-contents p {
          margin: 0;
        }

        .tiptap-blog-editor .callout-table-of-contents a {
          color: #0f172a;
          font-weight: 500;
          text-decoration: none;
        }

        .tiptap-blog-editor .callout-table-of-contents .table-of-contents-sublist a {
          color: #334155;
          font-size: 0.95rem;
          font-weight: 400;
        }

        .tiptap-blog-editor .callout-table-of-contents a:hover {
          text-decoration: underline;
        }

        .tiptap-blog-editor .callout-warning {
          background-color: #fef3c7;
          border-left: 4px solid #f59e0b;
        }

        .tiptap-blog-editor .callout-success {
          background-color: #d1fae5;
          border-left: 4px solid #10b981;
        }

        .tiptap-blog-editor .callout-error {
          background-color: #fee2e2;
          border-left: 4px solid #ef4444;
        }

        .tiptap-blog-editor .callout-quote,
        .tiptap-blog-editor .callout-expert-quote,
        .tiptap-blog-editor .callout-citation {
          background-color: #f3f4f6;
          border-left: 4px solid #22c55e;
          border-radius: 0 12px 12px 0;
          color: #111827;
          font-style: italic;
        }

        .tiptap-blog-editor .faq-schema {
          margin: 1.5rem 0;
        }

        .tiptap-blog-editor .faq-item {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          margin-bottom: 0.75rem;
          overflow: hidden;
        }

        .tiptap-blog-editor .faq-question {
          background-color: #f9fafb;
          padding: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .tiptap-blog-editor .faq-answer {
          padding: 1rem;
        }

        .tiptap-blog-editor .stats-grid {
          display: grid;
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .tiptap-blog-editor .stat-item {
          text-align: center;
          padding: 1.5rem;
          background-color: #f9fafb;
          border-radius: 0.5rem;
        }

        .tiptap-blog-editor .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: var(--primary, #3b82f6);
        }

        .tiptap-blog-editor .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        .tiptap-blog-editor .cta-block {
          margin: 1.5rem 0;
        }

        /* Image Resize Handles */
        .tiptap-blog-editor [data-resize-container] {
          position: relative;
          display: inline-block;
        }

        .tiptap-blog-editor [data-resize-wrapper] {
          position: relative;
          display: inline-block;
        }

        .tiptap-blog-editor [data-resize-handle] {
          position: absolute;
          width: 12px;
          height: 12px;
          background-color: #3b82f6;
          border: 2px solid white;
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.2s ease;
          z-index: 10;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        .tiptap-blog-editor [data-resize-container]:hover [data-resize-handle],
        .tiptap-blog-editor [data-resize-container][data-resize-state="true"] [data-resize-handle] {
          opacity: 1;
        }

        .tiptap-blog-editor [data-resize-handle="top-left"] {
          top: -6px;
          left: -6px;
          cursor: nwse-resize;
        }

        .tiptap-blog-editor [data-resize-handle="top-right"] {
          top: -6px;
          right: -6px;
          cursor: nesw-resize;
        }

        .tiptap-blog-editor [data-resize-handle="bottom-left"] {
          bottom: -6px;
          left: -6px;
          cursor: nesw-resize;
        }

        .tiptap-blog-editor [data-resize-handle="bottom-right"] {
          bottom: -6px;
          right: -6px;
          cursor: nwse-resize;
        }

        .tiptap-blog-editor [data-resize-container][data-resize-state="true"] {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .tiptap-blog-editor [data-resize-container] img {
          display: block;
          max-width: 100%;
        }

        /* Image alignment for resize wrapper - float the container, not the img */
        .tiptap-blog-editor [data-resize-container]:has(img.align-left) {
          float: left;
          margin-right: 1.5rem;
          margin-bottom: 1rem;
        }

        .tiptap-blog-editor [data-resize-container]:has(img.align-right) {
          float: right;
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }

        .tiptap-blog-editor [data-resize-container]:has(img.align-center) {
          display: flex;
          justify-content: center;
          margin-left: auto;
          margin-right: auto;
        }

        /* Bubble menu styles */
        .bubble-menu-toolbar,
        .image-bubble-menu {
          z-index: 9999 !important;
        }

        /* Ensure popovers inside bubble menu also have high z-index */
        .bubble-menu-toolbar [data-radix-popper-content-wrapper],
        .image-bubble-menu [data-radix-popper-content-wrapper],
        [data-radix-popper-content-wrapper] {
          z-index: 10000 !important;
        }

        /* Active block highlight is now rendered via ActiveBlockHighlight overlay
           in ContentEditorPage — never modify ProseMirror DOM attributes directly
           (MutationObserver causes infinite newlines). */
      `}</style>
    </Card>
  );
});

export default TipTapBlogEditor;
